import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { EventApi, EventContentArg, EventInput } from '@fullcalendar/core'

import { BuiltEvent } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { Address } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { ProviderCalendarBaseSlotSelectionModal } from './ProviderCalendarBaseSlotSelectionModal'
import {
  ProviderCalendarDateSlotSelectionContent,
  ProviderCalendarDateSlotSelectionContentRef,
} from '../providerCalendarSlotSelectionContent/ProviderCalendarDateSlotSelectionContent'

export type ProviderCalendarDateSlotSelectionModalProps = {
  addresses?: Address[] | null
  defaultVideoPlatform?: VIDEO_PLATFORM | null
  flexibleAvailabilityWindows?: boolean
  getEvents?: (params: { startDate: string; endDate: string }) => Promise<EventInput[]>
  eventToEdit?: EventContentArg | null
  initialEvents?: EventApi[] | null
  inPersonPreference?: boolean
  onConfirmationButtonPress: (events: BuiltEvent[]) => void
  splitVideoOptions?: boolean
  onRequestClose: () => void
  rangeToAdd?: { start: string; end: string; startLocal: string; endLocal: string } | null
  setShowAddEventModal: (showModal: boolean) => void
  showAddEventModal: boolean
  showModalitySelection?: boolean
  slotMaxTime?: string
  slotMinTime?: string
  videoPreference?: boolean
  isNPSession?: boolean
  /** Days to prevent from being selected in date input (0=Sunday, 6=Saturday) */
  hiddenDays?: number[]
}

export const ProviderCalendarDateSlotSelectionModal: FunctionComponent<ProviderCalendarDateSlotSelectionModalProps> = ({
  addresses,
  defaultVideoPlatform,
  flexibleAvailabilityWindows,
  eventToEdit,
  getEvents,
  initialEvents,
  inPersonPreference,
  splitVideoOptions,
  onConfirmationButtonPress,
  onRequestClose,
  rangeToAdd,
  setShowAddEventModal,
  showAddEventModal,
  showModalitySelection,
  slotMaxTime = '22:00:00',
  slotMinTime = '07:00:00',
  videoPreference,
  isNPSession,
  hiddenDays,
}) => {
  const { formatMessage } = useIntl()
  const contentRef = useRef<ProviderCalendarDateSlotSelectionContentRef>(null)
  const [isValid, setIsValid] = useState<boolean>(false)

  const confirmSelection = () => {
    const events = contentRef.current?.buildPayload() ?? []
    setShowAddEventModal(false)
    onConfirmationButtonPress(events)
  }

  const visibleRef = useRef(showAddEventModal)
  useEffect(() => {
    visibleRef.current = showAddEventModal
  }, [showAddEventModal])

  const handleAnimationEnd = () => {
    if (!visibleRef.current) {
      contentRef.current?.reset()
    }
  }

  return (
    <ProviderCalendarBaseSlotSelectionModal
      visible={showAddEventModal}
      onRequestClose={onRequestClose}
      handleAnimationEnd={handleAnimationEnd}
      onConfirmationButtonPress={confirmSelection}
      modalTitle={
        eventToEdit
          ? formatMessage({
              defaultMessage: 'Edit one-time availability',
              description: 'Title of modal to update an availability',
            })
          : formatMessage({
              defaultMessage: 'Add one-time availability',
              description: 'Title of modal to add an availability for this week to provider calendar',
            })
      }
      cancelButtonText={formatMessage({
        defaultMessage: 'Cancel',
        description: 'Button to cancel creating or editing event time and date',
      })}
      confirmationButtonText={
        eventToEdit
          ? formatMessage({ defaultMessage: 'Save', description: 'Button to save new event time and date' })
          : formatMessage({ defaultMessage: 'Add', description: 'Button to add new event time and date' })
      }
      confirmButtonDisabled={!isValid}
      modalContents={
        <ProviderCalendarDateSlotSelectionContent
          ref={contentRef}
          addresses={addresses}
          defaultVideoPlatform={defaultVideoPlatform}
          eventToEdit={eventToEdit}
          flexibleAvailabilityWindows={flexibleAvailabilityWindows}
          getEvents={getEvents}
          initialEvents={initialEvents}
          inPersonPreference={inPersonPreference}
          splitVideoOptions={splitVideoOptions}
          onValidityChange={setIsValid}
          rangeToAdd={rangeToAdd}
          showModalitySelection={showModalitySelection}
          slotMaxTime={slotMaxTime}
          slotMinTime={slotMinTime}
          videoPreference={videoPreference}
          isNPSession={isNPSession}
          hiddenDays={hiddenDays}
        />
      }
      testID={'ProviderCalendarDateSlotSelectionModal'}
    />
  )
}
